// The console's sections and which staff roles see each (DESIGN.md decision
// 1: analysts get dashboards and the map; moderators also the people-facing
// operations; owners everything). The server enforces the same split per
// operation (`x-roles`); this only decides what the shell shows.
import type { AdminSchemas } from "@10twenty/api/admin";

export type StaffRole = AdminSchemas["StaffRole"];

export const STAFF_ROLES: readonly StaffRole[] = ["owner", "moderator", "analyst"];

export type NavGroup = "Analytics" | "Operations" | "Administration";

export interface NavItem {
  /** Route path: "/" or one segment. */
  path: string;
  label: string;
  group: NavGroup;
  roles: readonly StaffRole[];
  /** The plan phase that builds the page, while it is still a placeholder. */
  arrives?: string;
}

const EVERYONE = STAFF_ROLES;
const OPERATORS: readonly StaffRole[] = ["owner", "moderator"];
const OWNERS: readonly StaffRole[] = ["owner"];

export const NAV: readonly NavItem[] = [
  { path: "/", label: "Overview", group: "Analytics", roles: EVERYONE },
  { path: "/growth", label: "Growth", group: "Analytics", roles: EVERYONE },
  { path: "/retention", label: "Retention", group: "Analytics", roles: EVERYONE },
  { path: "/features", label: "Features", group: "Analytics", roles: EVERYONE },
  { path: "/platforms", label: "Platforms", group: "Analytics", roles: EVERYONE },
  { path: "/map", label: "Map", group: "Analytics", roles: EVERYONE },
  // Analysts see the early access list as counts only.
  { path: "/early-access", label: "Early access", group: "Operations", roles: EVERYONE },
  { path: "/users", label: "Users", group: "Operations", roles: OPERATORS },
  { path: "/circles", label: "Circles", group: "Operations", roles: OPERATORS },
  { path: "/moderation", label: "Moderation", group: "Operations", roles: OPERATORS },
  { path: "/system", label: "System", group: "Operations", roles: OPERATORS },
  { path: "/settings", label: "Settings", group: "Administration", roles: OWNERS },
  { path: "/staff", label: "Staff", group: "Administration", roles: OWNERS },
  { path: "/audit", label: "Audit log", group: "Administration", roles: OWNERS },
];

export const NAV_GROUPS: readonly NavGroup[] = ["Analytics", "Operations", "Administration"];

export function canOpen(role: StaffRole, item: NavItem): boolean {
  return item.roles.includes(role);
}

/** The sections `role` sees, in nav order, grouped; empty groups are left out. */
export function navFor(role: StaffRole): { group: NavGroup; items: NavItem[] }[] {
  return NAV_GROUPS.map((group) => ({
    group,
    items: NAV.filter((item) => item.group === group && canOpen(role, item)),
  })).filter((section) => section.items.length > 0);
}

/** The section a pathname belongs to (its first segment), if any. */
export function navItemFor(pathname: string): NavItem | undefined {
  const segment = pathname.split("/").filter(Boolean)[0];
  const path = segment === undefined ? "/" : `/${segment}`;
  return NAV.find((item) => item.path === path);
}

export function roleLabel(role: StaffRole): string {
  return role === "owner" ? "Owner" : role === "moderator" ? "Moderator" : "Analyst";
}
